<template>
  <nx-fullscreen-dialog
    :active="active"
    :title="$t('message.wizard.new.' + this.type)"
    @dismiss="dismiss"
  >
    <template slot="toolbar-items">
      <slot name="toolbar" :validForm="validForm">
        <v-btn
          :loading="loading"
          @click="create"
          color="accent"
          dark
          flat
          :disabled="!validForm || loading"
        >{{ $t('message.wizard.createBtn') }}</v-btn>
      </slot>
    </template>

    <v-container py-3 fluid class="nx-content-wizard-body">
      <v-layout wrap fill-height justify-center>
        <v-flex class="xs12 sm10 md8">
          <nx-form class="nx-form" @form="flags = $event" ref="form">
            <div class="nx-form-fieldset">
              <nx-form-text
                :label="$t('message.form.fields.title')"
                name="title"
                ref="title"
                v-model="draft.title"
                validation="required|min:3"
              ></nx-form-text>

              <nx-taxonomy-form-selector
                :auth="taxonomyAuth"
                @authorized="onTaxonomyAuthorized"
                @input="onTaxonomyInput"
                v-model="draft.mainTaxonomyId"
                validation="required"
                v-bind="taxonomyFieldParams"
              ></nx-taxonomy-form-selector>

              <v-tooltip v-if="autoPublishSwitch" left :disabled="!autopublishDisabled">
                <v-switch
                  :disabled="autopublishDisabled"
                  :label="$t('message.content.tools.publish')"
                  ref="publish"
                  slot="activator"
                  v-model="draft.autopublish"
                ></v-switch>
                <span
                  v-if="autopublishErrorReason"
                  v-html="$t('message.content.auth.publish.' + autopublishErrorReason )"
                ></span>
              </v-tooltip>

              <slot :creationData="creationData()" :draft="draft"></slot>
            </div>
          </nx-form>
        </v-flex>
      </v-layout>
    </v-container>
  </nx-fullscreen-dialog>
</template>

<script>
import NxForm from '@/modules/form/Form';
import UploadImage from '@/modules/upload/UploadFile';
import NxFormText from '@/modules/form/components/FormText';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import toast from '@/modules/toast/services';
import authorizable from '@/modules/authorization/mixins/authorizable';
import loadable from '@/mixins/loadable';
import { mapGetters } from 'vuex';
import api from './api';
import wizardConfig from '@/modules/content/wizard/config/wizard';

export default {
  name: 'nx-content-wizard',
  inject: ['$wizardContext'],
  $_veeValidate: {
    validator: 'new', // give me a new vee-validate instance each time.
  },
  mixins: [authorizable, loadable],
  components: {
    NxFullscreenDialog,
    NxFormText,
    NxForm,
    [UploadImage.name]: UploadImage,
  },
  props: {
    active: Boolean,
    type: {
      type: String,
      required: true,
    },
    config: Object,
    autoPublishSwitch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      autopublishDisabled: false, // Will change depending of auth over selected taxonomy
      autopublishErrorReason: '',
      draft: {
        autopublish: true,
        title: null,
        mainTaxonomyId: null,
      },
      flags: {},
      navigateToEntityEditor: true,
    };
  },
  async mounted() {
    wizardConfig.fillDraft(this.type, this.draft, this.$wizardContext);
    // Load default autopublish state prop value
    const canPublish = await this.can('publish', {
      entity: 'content',
    });
    if (!canPublish) {
      this.draft.autopublish = false;
      this.autopublishDisabled = true;
      this.autopublishErrorReason = 'content';
    }
    const canPublishImage = await this.can('publish', {
      entity: 'content',
      type: this.type,
    });
    if (!canPublishImage) {
      this.draft.autopublish = false;
      this.autopublishDisabled = true;
      this.autopublishErrorReason = this.type;
    }
  },
  computed: {
    ...mapGetters(['siteId', 'context']),
    validForm() {
      return this.flags.valid;
    },
    taxonomyAuth() {
      return {
        actions: {
          ADD: 'create', // Specify bbnx operation when adding taxonomies for validation
        }, // Object of params that will be sent to bbnx to validate action
        params: {
          entity: 'content',
          type: this.type,
        },
      };
    },
    taxonomyFieldParams() {
      const defaultParams = {
        name: 'mainTaxonomyId',
        label: this.$t('message.wizard.category'),
        limit: 1,
        type: wizardConfig.config.mainTaxonTypes,
        additionalFilters: { archived: 'no' },
      };

      if (!this.config || !this.config.fieldsConfig) {
        return defaultParams;
      }

      const config = this.config.fieldsConfig.find(item => item.component === 'TaxonomySelectorField');
      if (!config) {
        return defaultParams;
      }
      return { ...defaultParams, ...config.params };
    },
  },
  methods: {
    edit(id) {
      this.$router.push({
        name: 'content.editor',
        params: { type: this.type, id },
      });
    },
    creationData() {
      return {
        ...this.draft,
        context: this.context,
      };
    },
    dismiss() {
      if (this.loading) {
        return;
      }
      this.draft.title = null;
      this.draft.mainTaxonomyId = null;
      this.$refs.form.resetFields();
      this.$emit('dismiss');
    },
    create() {
      this.$emit('create', this.draft);
    },
    async submit() {
      this.loadingStart();
      try {
        const response = await api.create(this.creationData(), this.type);
        this.loadingFinish();

        if (response) {
          this.success(response);
        } else {
          toast.notify(this.$t('message.wizard.createError'));
        }
      } finally {
        this.loadingFinish();
      }
    },
    updateField(field, value) {
      this.$set(this.draft, field, value);
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    success(response) {
      const entity = {
        ...response,
        route: {
          name: 'content.editor',
          params: { type: response.type, id: response.id },
        },
        navigateToEntityEditor: this.navigateToEntityEditor,
      };
      this.$emit('entityCreated', entity);
      toast.notify(this.$t('message.wizard.createSuccess'));
      this.$emit('dismiss');
    },
    onTaxonomyAuthorized(value) {
      if (!value) {
        toast.notify('message.content.auth.create.taxonomy');
      }
      // TODO: set taxonomy selector validation to false instead of displaying toast
    },
    async onTaxonomyInput(value) {
      // If is currently disabled by content or type, omit taxonomy authorization
      if (
        this.autopublishErrorReason
        && this.autopublishErrorReason !== 'category'
      ) {
        return;
      }

      // If there isn't value, set autopublish disabled and true
      if (!value) {
        this.draft.autopublish = true;
        this.autopublishDisabled = false;
        return;
      }

      // Check if use can publish on current section
      const canCreateOnTaxonomy = await this.can('create', {
        value,
        entity: 'content',
        type: this.type,
      });
      const canPublishOnTaxonomy = await this.can('publish', {
        value,
        entity: 'content',
        type: this.type,
      });
      // If user has permission to create on selected taxonomy, check if have to publish
      if (canCreateOnTaxonomy && !canPublishOnTaxonomy) {
        this.draft.autopublish = false;
        this.autopublishDisabled = true;
        this.autopublishErrorReason = 'category';
      }
    },
    validateForm() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) {
        if (value === true) {
          wizardConfig.fillDraft(this.type, this.draft, this.$wizardContext);
        }
        this.validateForm();
      },
    },
  },
};
</script>
